/**
 * JobMockAI — Lightweight API Client
 *
 * Generated for Step 22 (Final Hardening).
 * Mirrors the backend OpenAPI spec structure.
 * Uses the shared axios instance configured in AuthContext.
 *
 * Usage:
 *   import api from '../api/client';
 *   const { data } = await api.auth.login({ email, password });
 */
import axios from 'axios';
import { API_URL } from '../utils/apiConfig';

// Ensure API calls always target the backend (not the CRA dev server on :3000)
if (!axios.defaults.baseURL) {
  axios.defaults.baseURL = API_URL;
}

// ─── Helpers ──────────────────────────────────────────────────────
const get  = (url, config)       => axios.get(url, config);
const post = (url, data, config) => axios.post(url, data, config);
const put  = (url, data, config) => axios.put(url, data, config);
const patch = (url, data, config) => axios.patch(url, data, config);
const del  = (url, config)       => axios.delete(url, config);

// Small in-memory cache + in-flight dedupe helper for hot endpoints.
// TTL is intentionally short to avoid stale UX while deduping repeated requests.
const __cache = new Map(); // key -> { ts, data }
const __inflight = new Map(); // key -> Promise
const CACHE_TTL_MS = 5_000; // 5 seconds
function _cacheKeyForUrl(url, params) {
  if (!params) return url;
  try {
    const qs = new URLSearchParams(params).toString();
    return qs ? `${url}?${qs}` : url;
  } catch (e) {
    // fallback
    return `${url}:${JSON.stringify(params)}`;
  }
}
function _getCachedResponse(key) {
  const entry = __cache.get(key);
  if (!entry) return null;
  if (Date.now() - entry.ts > CACHE_TTL_MS) {
    __cache.delete(key);
    return null;
  }
  return entry.data;
}
function _setCachedResponse(key, data) {
  __cache.set(key, { ts: Date.now(), data });
}

// ─── Auth (/api/auth) ────────────────────────────────────────────
const auth = {
  register:       (body)     => post('/api/auth/register', body),
  login:          (body)     => post('/api/auth/login', body),
  verify:         ()         => get('/api/auth/verify'),
  logout:         ()         => post('/api/auth/logout'),
  getProfile:     ()         => get('/api/auth/profile'),
  updateProfile:  (body)     => put('/api/auth/profile', body),
  changePassword: (body)     => put('/api/auth/change-password', body),
  revokeSessions: ()         => post('/api/auth/revoke-sessions'),
  getDashboard:   ()         => get('/api/auth/dashboard'),
  verifyEmailOtp: (body)     => post('/api/auth/verify-email-otp', body),
  resendEmailOtp: (body)     => post('/api/auth/resend-email-otp', body),
  forgotPassword: (body)     => post('/api/auth/forgot-password', body),
  resetPassword:  (body)     => post('/api/auth/reset-password', body),
};

// ─── Interview V2 (/api/v1/interview) ────────────────────────────
const interview = {
  createSession:  (body)     => post('/api/v1/interview/sessions', body),
  listSessions:   (params)   => get('/api/v1/interview/sessions', { params }),
  getSession:     (id)       => get(`/api/v1/interview/sessions/${id}`),
  updateSession:  (id, body) => patch(`/api/v1/interview/sessions/${id}`, body),
  nextQuestion:   (id, body) => post(`/api/v1/interview/sessions/${id}/next-question`, body),
  submitAnswer:   (id, body) => post(`/api/v1/interview/sessions/${id}/responses`, body),
  shareSession:   (id, body) => post(`/api/v1/interview/sessions/${id}/share`, body),
  getShared:      (token)    => get(`/api/v1/interview/shared/${token}`),
  generateReport: (id)       => post(`/api/v1/interview/sessions/${id}/report`),
  // Step 9: Recording pre-signed URLs
  getRecordingUploadUrl:   (id, body) => post(`/api/v1/interview/sessions/${id}/recording-url`, body),
  getRecordingDownloadUrl: (id)       => get(`/api/v1/interview/sessions/${id}/recording-url`),
  // Quota status for InterviewFlow banner
  getQuotaStatus: ()         => get('/api/v1/interview/quota-status'),
};

// ─── Resume V2 (/api/v1/resume) ─────────────────────────────────
const resume = {
  upload:           (formData, config) => post('/api/v1/resume/upload', formData, { ...config, headers: { 'Content-Type': 'multipart/form-data' } }),
  getProfile:       ()                 => get('/api/v1/resume/profile'),
  list:             ()                 => get('/api/v1/resume/profiles'),
  get:              (id)               => get(`/api/v1/resume/${id}`),
  getStatus:        (id)               => get(`/api/v1/resume/${id}/status`),
  update:           (id, data)         => put(`/api/v1/resume/${id}`, data),
  delete:           (id)               => del(`/api/v1/resume/${id}`),
  setPrimary:       (id)               => put(`/api/v1/resume/${id}/primary`),
  analyze:          ()                 => post('/api/v2/resume/analyze'),
  getATSScore:      ()                 => get('/api/v2/resume/ats-score'),
  matchKeywords:    (body)             => post('/api/v2/resume/match-keywords', body),
  getOptimizations: ()                 => get('/api/v2/resume/optimizations'),
  applySuggestion:  (id)               => post(`/api/v2/resume/optimizations/${id}/apply`),
  getVersions:      ()                 => get('/api/v2/resume/versions'),
  restoreVersion:   (id)               => post(`/api/v2/resume/versions/${id}/restore`),
};

// ─── Billing (/api/v1/billing) ───────────────────────────────────
const billing = {
  getPlans:              ()     => get('/api/v1/billing/plans'),
  getSubscription:       ()     => get('/api/v1/billing/subscription'),
  cancelSubscription:    ()     => post('/api/v1/billing/subscription/cancel'),
  getHistory:            ()     => get('/api/v1/billing/history'),
  // PayPal
  paypalCreateSubscription: (body) => post('/api/v1/billing/paypal/create-subscription', body),
  paypalCreateOrder:        (body) => post('/api/v1/billing/paypal/create-order', body),
  paypalCaptureOrder:       (body) => post('/api/v1/billing/paypal/capture-order', body),
  // Razorpay
  razorpayCreateSubscription: (body) => post('/api/v1/billing/razorpay/create-subscription', body),
  razorpayCreateOrder:        (body) => post('/api/v1/billing/razorpay/create-order', body),
  razorpayVerifyPayment:      (body) => post('/api/v1/billing/razorpay/verify-payment', body),
};

// ─── AI (/api/v1/ai) ────────────────────────────────────────────
const ai = {
  complete:               (body) => post('/api/v1/ai/complete', body),
  getPrompts:             ()     => get('/api/v1/ai/prompts'),
  getPrompt:              (name) => get(`/api/v1/ai/prompts/${name}`),
  createPrompt:           (body) => post('/api/v1/ai/prompts', body),
  activatePromptVersion:  (name, version) => patch(`/api/v1/ai/prompts/${name}/activate/${version}`),
  deletePromptVersion:    (name, version) => del(`/api/v1/ai/prompts/${name}/versions/${version}`),
  getCacheStats:          ()     => get('/api/v1/ai/cache/stats'),
  invalidateCache:        (body) => post('/api/v1/ai/cache/invalidate', body),
  getCircuitBreakerStatus:()     => get('/api/v1/ai/circuit-breaker/status'),
};

// ─── Feature Flags (/api/v1/feature-flags) ──────────────────────
const featureFlags = {
  check:      (flagKey) => get(`/api/v1/feature-flags/check/${flagKey}`),
  checkBatch: (keys)    => get('/api/v1/feature-flags/check', { params: { keys: keys.join(',') } }),
};

// ─── Integrity monitoring (/api/integrity/v1) — fail-open client ─
const integrity = {
  startSubject: (body) => post('/api/integrity/v1/subjects/start', body),
  postEvents: (body) => post('/api/integrity/v1/events', body),
  complete: (body) => post('/api/integrity/v1/complete', body),
  getReport: (subjectId) => get(`/api/integrity/v1/reports/${encodeURIComponent(subjectId)}`),
  getSessionReport: (sessionId) =>
    get(`/api/integrity/v1/sessions/${encodeURIComponent(sessionId)}/report`),
  getBookingReport: (bookingId) =>
    get(`/api/integrity/v1/bookings/${encodeURIComponent(bookingId)}/report`),
  listCompanyCandidateSubjects: (companyId, candidateId, params) =>
    get(
      `/api/integrity/v1/companies/${encodeURIComponent(companyId)}/candidates/${encodeURIComponent(candidateId)}/subjects`,
      { params }
    ),
  getCompanyIntegritySummaries: (companyId, candidateIds) =>
    get(
      `/api/integrity/v1/companies/${encodeURIComponent(companyId)}/integrity-summaries`,
      {
        params: {
          candidateIds: Array.isArray(candidateIds) ? candidateIds.join(',') : candidateIds,
        },
      }
    ),
  getCompanyReport: (companyId, subjectId) =>
    get(
      `/api/integrity/v1/companies/${encodeURIComponent(companyId)}/reports/${encodeURIComponent(subjectId)}`
    ),
  exportCompanyPdf: (companyId, subjectId) =>
    get(
      `/api/integrity/v1/companies/${encodeURIComponent(companyId)}/reports/${encodeURIComponent(subjectId)}/export.pdf`,
      { responseType: 'blob' }
    ),
  exportSessionPdf: (sessionId) =>
    get(
      `/api/integrity/v1/sessions/${encodeURIComponent(sessionId)}/report/export.pdf`,
      { responseType: 'blob' }
    ),
  exportBookingPdf: (bookingId) =>
    get(
      `/api/integrity/v1/bookings/${encodeURIComponent(bookingId)}/report/export.pdf`,
      { responseType: 'blob' }
    ),
  getPendingChallenge: (subjectId) =>
    get(`/api/integrity/v1/subjects/${encodeURIComponent(subjectId)}/challenges/pending`),
  completeChallenge: (challengeId, body) =>
    post(`/api/integrity/v1/challenges/${encodeURIComponent(challengeId)}/complete`, body || {}),
  createMediaUploadUrl: (body) => post('/api/integrity/v1/media/upload-url', body),
  confirmMediaUpload: (mediaAssetId, body) =>
    post(`/api/integrity/v1/media/${encodeURIComponent(mediaAssetId)}/confirm`, body || {}),
  addNote: (subjectId, note) =>
    post(`/api/integrity/v1/reports/${encodeURIComponent(subjectId)}/notes`, { note }),
  setStatus: (subjectId, status) =>
    patch(`/api/integrity/v1/reports/${encodeURIComponent(subjectId)}/status`, { status }),
  setCompanyStatus: (companyId, subjectId, status) =>
    patch(
      `/api/integrity/v1/companies/${encodeURIComponent(companyId)}/reports/${encodeURIComponent(subjectId)}/status`,
      { status }
    ),
  submitAppeal: (subjectId, reason) =>
    post(`/api/integrity/v1/reports/${encodeURIComponent(subjectId)}/appeals`, { reason }),
  resolveAppeal: (companyId, subjectId, appealId, body) =>
    post(
      `/api/integrity/v1/companies/${encodeURIComponent(companyId)}/reports/${encodeURIComponent(subjectId)}/appeals/${encodeURIComponent(appealId)}/resolve`,
      body || {}
    ),
};

// ─── Learning Hub (/api/v1/learning) ─────────────────────────────
const learning = {
  // Recommendations (existing)
  getRecommendations:    (params) => get('/api/v1/learning/recommendations', { params }),
  dismissRecommendation: (id)     => patch(`/api/v1/learning/recommendations/${id}/dismiss`),
  regenerateRecommendations: ()   => post('/api/v1/learning/recommendations/regenerate'),
  logEngagement:         (body)   => post('/api/v1/learning/engagement', body),

  // Learning Paths (Step 4)
  getPaths:              (params) => get('/api/v1/learning/paths', { params }),
  getPath:               (id)     => get(`/api/v1/learning/paths/${id}`),
  getPathProgress:       (id)     => get(`/api/v1/learning/paths/${id}/progress`),
  completeModule:        (pathId, moduleId) => post(`/api/v1/learning/paths/${pathId}/modules/${moduleId}/complete`),
  getModuleQuiz:         (pathId, moduleId) => get(`/api/v1/learning/paths/${pathId}/modules/${moduleId}/quiz`),
  submitQuiz:            (pathId, moduleId, body) => post(`/api/v1/learning/paths/${pathId}/modules/${moduleId}/quiz`, body),

  // Resources (Step 4)
  getResources:          (params) => get('/api/v1/learning/resources', { params }),
  getResource:           (id)     => get(`/api/v1/learning/resources/${id}`),
  getRelatedResources:   (id)     => get(`/api/v1/learning/resources/${id}/related`),
  markResourceComplete:  (id)     => post(`/api/v1/learning/resources/${id}/complete`),

  // Bookmarks (Step 4)
  getBookmarks:          ()       => get('/api/v1/learning/bookmarks'),
  addBookmark:           (id)     => post(`/api/v1/learning/bookmarks/${id}`),
  removeBookmark:        (id)     => del(`/api/v1/learning/bookmarks/${id}`),

  // Certificates (Step 4)
  getCertificate:        (pathId) => get(`/api/v1/learning/paths/${pathId}/certificate`),
  downloadCertificate:   (id)     => get(`/api/v1/learning/certificates/${id}/download`, { responseType: 'blob' }),

  // Progress (Step 4)
  getOverallProgress:    ()       => get('/api/v1/learning/progress'),
};

// ─── Progress Dashboard (/api/v1/progress) ───────────────────────
const progress = {
  getDashboard: () => {
    const key = _cacheKeyForUrl('/api/v1/progress/dashboard');
    const cached = _getCachedResponse(key);
    if (cached) return Promise.resolve(cached);
    if (__inflight.has(key)) return __inflight.get(key);
    const p = get('/api/v1/progress/dashboard')
      .then((res) => { _setCachedResponse(key, res); __inflight.delete(key); return res; })
      .catch((err) => { __inflight.delete(key); throw err; });
    __inflight.set(key, p);
    return p;
  },
  getBadges:    ()       => get('/api/v1/progress/badges'),
  getHistory:   (params) => get('/api/v1/progress/history', { params }),
  // V2 endpoints (Phase 4 Step 8)
  getReadinessScore:     ()           => get('/api/v2/progress/readiness-score'),
  getReadinessHistory:   (params)     => get('/api/v2/progress/readiness-history', { params }),
  getSkills:             ()           => get('/api/v2/progress/skills'),
  getSkillHistory:       (category)   => get(`/api/v2/progress/skills/${category}/history`),
  getMilestones:         ()           => get('/api/v2/progress/milestones'),
  getRecentMilestones:   ()           => get('/api/v2/progress/milestones/recent'),
  getHeatmap:            ()           => get('/api/v2/progress/heatmap'),
  getWeeklyComparison:   ()           => get('/api/v2/progress/weekly-comparison'),
  getWeeklyHistory:      (params)     => get('/api/v2/progress/weekly-history', { params }),
  getAIInsights:         ()           => get('/api/v2/progress/ai-insights'),
  markInsightRead:       (id)         => post(`/api/v2/progress/ai-insights/${id}/read`),
  getBadgesV2:           ()           => get('/api/v2/progress/badges'),
  getBadgeDetail:        (id)         => get(`/api/v2/progress/badges/${id}`),
};

// ─── Notifications (/api/v1/notifications) ───────────────────────
const notifications = {
  list:            (params)   => get('/api/v1/notifications', { params }),
  getUnread:       ()         => get('/api/v1/notifications/unread'),
  getUnreadCount:  ()         => get('/api/v1/notifications/unread-count'),
  getPreferences:  ()         => get('/api/v1/notifications/preferences'),
  markRead:        (id)       => patch(`/api/v1/notifications/${id}/read`),
  markAllAsRead:   ()         => patch('/api/v1/notifications/read-all'),
  updatePreferences: (body)   => post('/api/v1/notifications/preferences', body),
};

// ─── Community (/api/v1/community) ───────────────────────────────
const community = {
  createProfile:    (body)  => post('/api/v1/community/profile', body),
  updateProfile:    (body)  => put('/api/v1/community/profile', body),
  getProfile:       ()      => get('/api/v1/community/profile'),
  setVisibility:    (body)  => patch('/api/v1/community/profile/visibility', body),
  discover:         (params) => {
    const key = _cacheKeyForUrl('/api/v1/community/discover', params);
    const cached = _getCachedResponse(key);
    if (cached) {
      return Promise.resolve(cached);
    }
    // If there's an in-flight identical request, return that promise.
    if (__inflight.has(key)) return __inflight.get(key);

    const p = get('/api/v1/community/discover', { params })
      .then((res) => {
        // store response in cache (full axios response)
        _setCachedResponse(key, res);
        __inflight.delete(key);
        return res;
      })
      .catch((err) => {
        __inflight.delete(key);
        throw err;
      });

    __inflight.set(key, p);
    return p;
  },
  getUserProfile:   (userId)=> get(`/api/v1/community/profiles/${userId}`),
  // Connection requests
  sendRequest:      (body)  => post('/api/v1/community/connections/request', body),
  getReceivedRequests: ()   => get('/api/v1/community/connections/requests/received'),
  getSentRequests:  ()      => get('/api/v1/community/connections/requests/sent'),
  acceptRequest:    (id, body) => patch(`/api/v1/community/connections/requests/${id}/accept`, body),
  rejectRequest:    (id)    => patch(`/api/v1/community/connections/requests/${id}/reject`),
  cancelRequest:    (id)    => del(`/api/v1/community/connections/requests/${id}`),
  listConnections:  ()      => get('/api/v1/community/connections'),
  removeConnection: (id)    => del(`/api/v1/community/connections/${id}`),
  getQuota:         ()      => get('/api/v1/community/connections/quota'),
  getQuotaStatus:   ()      => get('/api/v1/community/quota-status'),
  purchaseQuota:    (body)  => post('/api/v1/community/connections/quota/purchase', body),
  capturePaypal:    (body)  => post('/api/v1/community/connections/quota/purchase/capture-paypal', body),
  verifyRazorpay:   (body)  => post('/api/v1/community/connections/quota/purchase/verify-razorpay', body),
  // Activity feed
  getActivityFeed:  (params) => {
    const key = _cacheKeyForUrl('/api/v1/community/activity', params);
    const cached = _getCachedResponse(key);
    if (cached) return Promise.resolve(cached);
    if (__inflight.has(key)) return __inflight.get(key);
    const p = get('/api/v1/community/activity', { params })
      .then((res) => { _setCachedResponse(key, res); __inflight.delete(key); return res; })
      .catch((err) => { __inflight.delete(key); throw err; });
    __inflight.set(key, p);
    return p;
  },
  // Block / unblock
  blockUser:        (userId)=> post(`/api/v1/community/users/${userId}/block`),
  unblockUser:      (userId)=> del(`/api/v1/community/users/${userId}/block`),
  getBlockedUsers:  ()      => get('/api/v1/community/blocked-users'),
  // Community feed posts
  getPosts:           ({ limit } = {}) => {
    const params = new URLSearchParams();
    if (limit) params.append('limit', limit);
    const qs = params.toString();
    return get(`/api/v1/community/posts${qs ? `?${qs}` : ''}`);
  },
  createPost:         (data)      => post('/api/v1/community/posts', data),
  getPostMediaUploadUrl: (body) => post('/api/v1/community/posts/media-upload-url', body),
  getPostMediaLimits: () => get('/api/v1/community/posts/media-limits'),
  likePost:           (postId)    => post(`/api/v1/community/posts/${postId}/like`),
  getComments:        (postId)    => get(`/api/v1/community/posts/${postId}/comments`),
  createComment:      (postId, d) => post(`/api/v1/community/posts/${postId}/comments`, d),
  updatePost:         (id, d)     => put(`/api/v1/community/posts/${id}`, d),
  deletePost:         (id)        => del(`/api/v1/community/posts/${id}`),
  updateComment:      (postId, commentId, d) => put(`/api/v1/community/posts/${postId}/comments/${commentId}`, d),
  deleteComment:      (postId, commentId)    => del(`/api/v1/community/posts/${postId}/comments/${commentId}`),
  // 1:1 DMs (connected peers — REST + polling)
  openDmThread:       (body)  => post('/api/v1/community/messages/threads', body),
  listDmThreads:      ()      => get('/api/v1/community/messages/threads'),
  listDmMessages:     (threadId, params) => get(`/api/v1/community/messages/threads/${threadId}/messages`, { params }),
  sendDmMessage:      (threadId, body) => post(`/api/v1/community/messages/threads/${threadId}/messages`, body),
};

// ─── Career Growth (/api/v1/career) ──────────────────────────────
const career = {
  getSnapshots:        (params) => get('/api/v1/career/snapshots', { params }),
  generateSnapshot:    ()       => post('/api/v1/career/snapshots/generate'),
  getSnapshot:         (id)     => get(`/api/v1/career/snapshots/${id}`),
  getGrowthTrajectory: ()       => get('/api/v1/career/growth-trajectory'),
  getRoadmap:          ()       => get('/api/v1/career/roadmap'),
  getSkillGaps:        ()       => get('/api/v1/career/skill-gaps'),
  getSalaryInsights:   (params) => get('/api/v1/career/salary-insights', { params }),
  getReadinessScore:   ()       => get('/api/v1/career/readiness-score'),
};

// ─── Account (/api/v1/account) ───────────────────────────────────
const account = {
  deleteAccount:      (body)     => del('/api/v1/account', { data: body }),
  cancelDeletion:     ()         => post('/api/v1/account/cancel-deletion'),
  exportData:         ()         => post('/api/v1/account/export'),
  uploadAvatar:       (formData) => post('/api/v1/account/avatar', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  getProfileVideo:            ()       => get('/api/v1/account/profile-video'),
  createProfileVideoUploadUrl:(body)   => post('/api/v1/account/profile-video/upload-url', body),
  confirmProfileVideo:        (body)   => post('/api/v1/account/profile-video/confirm', body),
  updateProfileVideoSharing:  (body)   => patch('/api/v1/account/profile-video/sharing', body),
  deleteProfileVideo:         ()       => del('/api/v1/account/profile-video'),
};

// ─── Demo Studio (/api/demo-studio) ──────────────────────────────
const demoStudio = {
  getScripts:   ()       => get('/api/demo-studio/scripts'),
  generateVoiceover: (body) => post('/api/demo-studio/voiceover', body, { responseType: 'blob' }),
  transcribe: (body) => post('/api/demo-studio/transcribe', body),
  polishScript: (body) => post('/api/demo-studio/polish-script', body),
  createMergeJob: (formData) => post('/api/demo-studio/merge-jobs', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  getMergeJob: (jobId) => get(`/api/demo-studio/merge-jobs/${jobId}`),
};

const games = {
  getDaily: (gameType, date) =>
    get(`/api/v1/games/daily/${gameType}`, { params: date ? { date } : {} }),
  getDailyStatus: (gameType, date) =>
    get(`/api/v1/games/daily/${gameType}/status`, { params: date ? { date } : {} }),
  submitDaily: ({ gameType, ...body }) => {
    const pathMap = {
      mini_sudoku: 'mini-sudoku',
      queens: 'queens',
      binary_grid: 'binary',
    };
    const segment = pathMap[gameType] || gameType;
    return post(`/api/v1/games/daily/${segment}/submit`, { ...body, gameType });
  },
  // Legacy helpers
  getDailyMiniSudoku: (date) =>
    get('/api/v1/games/daily/mini-sudoku', { params: date ? { date } : {} }),
};

// ─── Admin V1 (/api/v1/admin) ────────────────────────────────────
const admin = {
  getFeatureFlags:     ()       => get('/api/v1/admin/feature-flags'),
  updateFeatureFlag:   (id, body) => patch(`/api/v1/admin/feature-flags/${id}`, body),
  listFeatureFlagOverrides: (params) => get('/api/v1/admin/feature-flags/overrides', { params }),
  upsertFeatureFlagOverride: (body) => put('/api/v1/admin/feature-flags/overrides', body),
  deleteFeatureFlagOverride: (params) => del('/api/v1/admin/feature-flags/overrides', { params }),
  listUsers:           (params) => get('/api/v1/admin/users', { params }),
  updateUser:          (id, body) => patch(`/api/v1/admin/users/${id}`, body),
  deleteUser:          (id)     => del(`/api/v1/admin/users/${id}`),
  restoreUser:         (id)     => post(`/api/v1/admin/users/${id}/restore`),
  getAuditLogs:        (params) => get('/api/v1/admin/audit-logs', { params }),
  getAiUsageSummary:   (params) => get('/api/v1/admin/ai-usage/summary', { params }),
  getUserAiUsage:      (id, params) => get(`/api/v1/admin/users/${id}/ai-usage`, { params }),
  createAgentGrant:    (id, body) => post(`/api/v1/admin/users/${id}/agent-grants`, body),
  listAgentGrants:     (id, params) => get(`/api/v1/admin/users/${id}/agent-grants`, { params }),
  getPlans:            ()       => get('/api/v1/admin/plans'),
  updatePlanQuotas:    (slug, body) => patch(`/api/v1/admin/plans/${slug}`, body),
  listStudyCatalog:    (params) => get('/api/v1/admin/study-catalog', { params }),
  createStudyCatalogEntry: (body) => post('/api/v1/admin/study-catalog', body),
  updateStudyCatalogEntry: (id, body) => patch(`/api/v1/admin/study-catalog/${id}`, body),
  listStudyCatalogMisses: (params) => get('/api/v1/admin/study-catalog-misses', { params }),
  ignoreStudyCatalogMiss: (id) => post(`/api/v1/admin/study-catalog-misses/${id}/ignore`),
  resolveStudyCatalogMiss: (id, body) => post(`/api/v1/admin/study-catalog-misses/${id}/resolve`, body),
  listMediaModeration: (params) => get('/api/v1/admin/media-moderation', { params }),
  decideMediaModeration: (body) => post('/api/v1/admin/media-moderation/decide', body),
  getVideoLimits:      ()       => get('/api/v1/admin/video-limits'),
  updateVideoLimits:   (body)   => put('/api/v1/admin/video-limits', body),
  getCompanies:        (params) => get('/api/v1/hiring/admin/companies', { params }),
  verifyCompany:       (id, body) => patch(`/api/v1/hiring/admin/companies/${id}`, body),
};

// ─── Partners (/api/v1/partners) ─────────────────────────────────
const partners = {
  create:       (body)  => post('/api/v1/partners', body),
  list:         ()      => get('/api/v1/partners'),
  get:          (id)    => get(`/api/v1/partners/${id}`),
  update:       (id, body) => put(`/api/v1/partners/${id}`, body),
  delete:       (id)    => del(`/api/v1/partners/${id}`),
  generateKey:  (id)    => post(`/api/v1/partners/${id}/api-key`),
  listKeys:     (id)    => get(`/api/v1/partners/${id}/api-keys`),
  revokeKey:    (id, keyId) => del(`/api/v1/partners/${id}/api-keys/${keyId}`),
};

// ─── Rewards (/api/v1/rewards) ───────────────────────────────────
const rewards = {
  getStore:      ()      => get('/api/v1/rewards/store'),
  getItem:       (id)    => get(`/api/v1/rewards/store/${id}`),
  createItem:    (body)  => post('/api/v1/rewards/store', body),
  updateItem:    (id, body) => put(`/api/v1/rewards/store/${id}`, body),
  deleteItem:    (id)    => del(`/api/v1/rewards/store/${id}`),
  redeem:        (body)  => post('/api/v1/rewards/redeem', body),
  getRedemptions:()      => get('/api/v1/rewards/redemptions'),
  getBalance:    ()      => get('/api/v1/rewards/balance'),
  adjustBalance: (body)  => post('/api/v1/rewards/adjust-balance', body),
};

// ─── Feedback (/api/feedback) ────────────────────────────────────
const feedback = {
  getCategories: ()      => get('/api/feedback/categories'),
  submit:        (body)  => post('/api/feedback/submit', body),
  getMy:         (params) => get('/api/feedback/my', { params: { limit: 100, ...params } }),
  getPublic:     (params)=> get('/api/feedback/public', { params }),
  vote:          (id)    => post(`/api/feedback/${id}/vote`),
  // Admin
  adminList:     (params)=> get('/api/feedback/admin/all', { params }),
  adminSetStatus:(id, body) => put(`/api/feedback/admin/${id}/status`, body),
  adminAnalytics:()      => get('/api/feedback/admin/analytics'),
};

// ─── Reviews (/api/reviews) ──────────────────────────────────────
const reviews = {
  getAll:      (params) => get('/api/reviews', { params }),
  getStats:    ()       => get('/api/reviews/stats'),
  getById:     (id)     => get(`/api/reviews/${id}`),
  create:      (body)   => post('/api/reviews', body),
  update:      (id, body) => put(`/api/reviews/${id}`, body),
  remove:      (id)     => del(`/api/reviews/${id}`),
  vote:        (id, body) => post(`/api/reviews/${id}/vote`, body),
  getMy:       ()       => get('/api/reviews/user/my-reviews'),
};

// ─── Analytics (admin) (/api/analytics) ──────────────────────────
const analytics = {
  getOverview:       (params)=> get('/api/analytics/overview', { params }),
  getActiveSessions: (params)=> get('/api/analytics/active-sessions', { params }),
  getUserEngagement: (params)=> get('/api/analytics/user-engagement', { params }),
  getSystemMetrics:  ()      => get('/api/analytics/system-metrics'),
};

// ─── Legal / Consent (/api/v1/legal) ─────────────────────────────
const legal = {
  getActiveDocument:   (type)         => get(`/api/v1/legal/documents/${type}/active`),
  getDocumentTypes:    ()             => get('/api/v1/legal/documents/types'),
  getFlowDocuments:    (flow)         => get(`/api/v1/legal/flow/${flow}`),
  getFlowConsentStatus:(flow)         => get(`/api/v1/legal/flow/${flow}/status`),
  getPending:          ()             => get('/api/v1/legal/pending'),
  getUserConsents:     ()             => get('/api/v1/legal/user-consents'),
  getConsentSnapshot:  (id)           => get(`/api/v1/legal/user-consents/${id}/snapshot`),
  acceptConsent:       (body)         => post('/api/v1/legal/accept', body),
  declineConsent:      (documentId)   => post('/api/v1/legal/decline', { documentId }),
  getAllDocuments:     ()             => get('/api/v1/legal/admin/documents'),
  getDocumentsByType:  (type)         => get(`/api/v1/legal/admin/documents/by-type/${type}`),
  createDocument:      (body)         => post('/api/v1/legal/admin/documents', body),
  updateDocument:      (id, body)     => patch(`/api/v1/legal/admin/documents/${id}`, body),
  publishDocument:     (documentId)   => post('/api/v1/legal/admin/documents/publish', { documentId }),
  archiveDocument:     (id)           => post(`/api/v1/legal/admin/documents/${id}/archive`),
  getAuditEvents:      (params)       => get('/api/v1/legal/admin/audit-events', { params }),
  getAdminConsents:    (params)       => get('/api/v1/legal/admin/user-consents', { params }),
  validateHash:        (body)         => post('/api/v1/legal/admin/validate-hash', body),
};

// ─── Privacy (/api/privacy) ──────────────────────────────────────
const privacy = {
  requestDeletion: (body) => post('/api/privacy/request-deletion', body),
  confirmDeletion: (body) => post('/api/privacy/confirm-deletion', body),
  getStatus:       ()     => get('/api/privacy/deletion-status'),
};

// ─── TTS / STT ──────────────────────────────────────────────────
const tts = {
  synthesize: (body) => post('/api/tts/synthesize', body),
  getVoices:  ()     => get('/api/tts/elevenlabs/voices'),
};

const stt = {
  transcribe: (formData, config) => post('/api/stt/transcribe', formData, config),
};

// ─── Leaderboard & XP (Step 26) ─────────────────────────────────
const leaderboard = {
  get:        (params) => get('/api/v1/leaderboard', { params }),
  myRank:     ()       => get('/api/v1/leaderboard/my-rank'),
  // V2 endpoints (Phase 4 Step 8)
  getGlobal:  (params) => get('/api/v2/leaderboard/global',   { params }),
  getWeekly:  (params) => get('/api/v2/leaderboard/weekly',   { params }),
  getBySkill: (cat, p) => get(`/api/v2/leaderboard/skill/${cat}`, { params: p }),
  getMyRanks: ()       => get('/api/v2/leaderboard/my-ranks'),
};

const xp = {
  get:       (params) => get('/api/v1/xp', { params }),
  breakdown: ()       => get('/api/v1/xp/breakdown'),
};

const achievements = {
  getAll: ()   => get('/api/v1/achievements'),
  get:   (id)  => get(`/api/v1/achievements/${id}`),
};

// ─── Rooms (Step 27) ────────────────────────────────────────────
const rooms = {
  create:       (body)      => post('/api/v1/rooms', body),
  get:          (roomId)    => get(`/api/v1/rooms/${roomId}`),
  joinByCode:   (roomCode)  => post(`/api/v1/rooms/join/${roomCode}`),
  start:        (roomId)    => patch(`/api/v1/rooms/${roomId}/start`),
  end:          (roomId)    => patch(`/api/v1/rooms/${roomId}/end`),
  updateRecordingMetadata: (roomId, body) => patch(`/api/v1/rooms/${roomId}/recording-metadata`, body),
  getMyRooms:   (params)    => get('/api/v1/rooms/my', { params }),
  logEvent:     (roomId, body) => post(`/api/v1/rooms/${roomId}/events`, body),
  getEvents:    (roomId)    => get(`/api/v1/rooms/${roomId}/events`),
  submitFeedback: (roomId, body) => post(`/api/v1/rooms/${roomId}/feedback`, body),
  getFeedback:  (roomId)    => get(`/api/v1/rooms/${roomId}/feedback`),
};

// ─── AI Observer (Step 29) ──────────────────────────────────────
const aiObserver = {
  updateConsent:  (roomId, body)    => post(`/api/v1/rooms/${roomId}/ai-observer/consent`, body),
  start:          (roomId, body)    => post(`/api/v1/rooms/${roomId}/ai-observer/start`, body),
  submitTranscript: (roomId, body)  => post(`/api/v1/rooms/${roomId}/ai-observer/transcript`, body),
  complete:       (roomId)          => post(`/api/v1/rooms/${roomId}/ai-observer/complete`),
  getReport:      (roomId)          => get(`/api/v1/rooms/${roomId}/ai-observer/report`),
  getMyObservations: (params)       => get('/api/v1/ai-observations/my', { params }),
  // SSE hint stream helper
  createHintStream: (roomId, token) => {
    const url = `${process.env.REACT_APP_API_URL || ''}/api/v1/rooms/${roomId}/ai-observer/hints`;
    return new EventSource(url, { headers: { Authorization: `Bearer ${token}` } });
  },
};

// ─── Scheduling (Step 28) ───────────────────────────────────────
const scheduling = {
  create:         (body)       => post('/api/v1/schedules', body),
  getAll:         (params)     => get('/api/v1/schedules', { params }),
  getSchedules:   (params)     => get('/api/v1/schedules', { params }),
  get:            (id)         => get(`/api/v1/schedules/${id}`),
  confirm:        (id)         => patch(`/api/v1/schedules/${id}/confirm`),
  cancel:         (id, body)   => patch(`/api/v1/schedules/${id}/cancel`, body),
  startRoom:      (id)         => post(`/api/v1/schedules/${id}/start-room`),
  setAvailability: (body)      => put('/api/v1/availability', body),
  getAvailability: ()          => get('/api/v1/availability'),
  getPeerAvailability: (userId) => get(`/api/v1/availability/${userId}`),
  getAvailableSlots: (userId, params) => get(`/api/v1/schedules/available-slots/${userId}`, { params }),
};

// ─── Replay (Step 30) ──────────────────────────────────────────
const replay = {
  getReplayData:    (sessionId)                   => get(`/api/v1/replay/sessions/${sessionId}`),
  triggerAnalysis:  (sessionId)                   => post(`/api/v1/replay/sessions/${sessionId}/analyze`),
  getAnalysis:      (sessionId)                   => get(`/api/v1/replay/sessions/${sessionId}/analysis`),
  addAnnotation:    (sessionId, body)             => post(`/api/v1/replay/sessions/${sessionId}/annotations`, body),
  getAnnotations:   (sessionId)                   => get(`/api/v1/replay/sessions/${sessionId}/annotations`),
  deleteAnnotation: (annotationId)                => del(`/api/v1/replay/annotations/${annotationId}`),
  compareSessions:  (sessionId, compareSessionId) => get(`/api/v1/replay/sessions/${sessionId}/compare/${compareSessionId}`),
};

// ─── Referrals (Step 31) ────────────────────────────────────────
const referrals = {
  getMyCode:       ()             => get('/api/v1/referrals/my-code'),
  applyCode:       (body)         => post('/api/v1/referrals/apply', body),
  getStats:        ()             => get('/api/v1/referrals/stats'),
  getHistory:      (params)       => get('/api/v1/referrals/history', { params }),
  getMilestones:   ()             => get('/api/v1/referrals/milestones'),
  getLeaderboard:  ()             => get('/api/v1/referrals/leaderboard'),
};

// ─── Marketplace (Step 32 + Phase 4 Step 5) ────────────────────
const marketplace = {
  getListings:         (params)            => get('/api/v1/marketplace/listings', { params }),
  getRecommended:      ()                  => get('/api/v1/marketplace/recommended'),
  getListingDetail:    (id)                => get(`/api/v1/marketplace/listings/${id}`),
  getAvailableSlots:   (listingId, params) => get(`/api/v1/marketplace/listings/${listingId}/available-slots`, { params }),
  applyAsInterviewer:  (body)              => post('/api/v1/marketplace/apply-interviewer', body),
  draftInterviewerProfile: (body)          => post('/api/v1/marketplace/draft-interviewer-profile', body),
  getMyProfile:        ()                  => get('/api/v1/marketplace/my-profile'),
  createListing:       (body)              => post('/api/v1/marketplace/listings', body),
  generateListingDrafts: (body)            => post('/api/v1/marketplace/listings/generate-drafts', body || {}),
  submitListingDraft:  (id)                => post(`/api/v1/marketplace/listings/${id}/submit`),
  getMyListings:       ()                  => get('/api/v1/marketplace/my-listings'),
  getMyEarnings:       ()                  => get('/api/v1/marketplace/my-earnings'),
  requestPayout:       ()                  => post('/api/v1/marketplace/my-payout-request'),
  getMyPayouts:        ()                  => get('/api/v1/marketplace/my-payouts'),
  submitSessionFeedback: (bookingId, body) => post(`/api/v1/marketplace/bookings/${bookingId}/session-feedback`, body),
  getSessionFeedback:  (bookingId)         => get(`/api/v1/marketplace/bookings/${bookingId}/session-feedback`),
  updateListing:       (id, body)          => put(`/api/v1/marketplace/listings/${id}`, body),
  deleteListing:       (id)                => del(`/api/v1/marketplace/listings/${id}`),
  bookSession:         (body)              => post('/api/v1/marketplace/bookings', body),
  confirmBooking:      (id, body)          => post(`/api/v1/marketplace/bookings/${id}/confirm`, body),
  createPaymentOrder:  (id, body)          => post(`/api/v1/marketplace/bookings/${id}/payment-order`, body),
  cancelBooking:       (id, body)          => post(`/api/v1/marketplace/bookings/${id}/cancel`, body),
  completeBooking:     (id)                => post(`/api/v1/marketplace/bookings/${id}/complete`),
  submitReview:        (bookingId, body)   => post(`/api/v1/marketplace/bookings/${bookingId}/review`, body),
  getReview:           (bookingId)         => get(`/api/v1/marketplace/bookings/${bookingId}/review`),
  setMeetingLink:      (id, meetingLink)   => patch(`/api/v1/marketplace/bookings/${id}/meeting-link`, { meetingLink }),
  setMeetingMode:      (id, meetingMode)   => patch(`/api/v1/marketplace/bookings/${id}/meeting-mode`, { meetingMode }),
  updateRecordingMetadata: (id, body)       => patch(`/api/v1/marketplace/bookings/${id}/recording-metadata`, body),
  recordIdShow:        (id, body)           => post(`/api/v1/marketplace/bookings/${id}/id-show`, body || {}),
  submitIdentityAttestation: (id, body)     => post(`/api/v1/marketplace/bookings/${id}/identity-attestation`, body || {}),
  // Phase 4 Step 5 additions
  getBookings:         (params)            => get('/api/v1/marketplace/bookings', { params }),
  getCategories:       ()                  => get('/api/v1/marketplace/categories'),
  getFeaturedServices: ()                  => get('/api/v1/marketplace/featured'),
  getProviderProfile:  (id)                => get(`/api/v1/marketplace/providers/${id}`),
  getSimilarServices:  (id)                => get(`/api/v1/marketplace/listings/${id}/similar`),
  rescheduleBooking:   (id, body)          => post(`/api/v1/marketplace/bookings/${id}/reschedule`, body),
  /** Admin: list marketplace interviewer applications (verification queue) */
  adminListInterviewerApplications: (params) =>
    get('/api/v1/marketplace/admin/applications', { params }),
  adminVerifyInterviewerApplication: (profileId, body) =>
    patch(`/api/v1/marketplace/admin/applications/${profileId}/verify`, body),
  /** Admin: listing approval queue */
  adminListListings: (params) =>
    get('/api/v1/marketplace/admin/listings', { params }),
  adminUpdateListingStatus: (listingId, body) =>
    patch(`/api/v1/marketplace/admin/listings/${listingId}/status`, body),
  adminGenerateListingDrafts: (userId, body) =>
    post(`/api/v1/marketplace/admin/interviewers/${userId}/generate-listing-drafts`, body || {}),
  adminProvisionListings: (userId, body) =>
    post(`/api/v1/marketplace/admin/interviewers/${userId}/provision-listings`, body || {}),
  /** Admin: payout management */
  adminListPayouts: (params) =>
    get('/api/v1/marketplace/admin/payouts', { params }),
  adminUpdatePayoutStatus: (payoutId, body) =>
    patch(`/api/v1/marketplace/admin/payouts/${payoutId}`, body),
};

// ─── Interviewer Self-Service ───────────────────────────────────
const interviewer = {
  getProfile:          ()                  => get('/api/v1/interviewer/profile'),
  updateProfile:       (body)              => put('/api/v1/interviewer/profile', body),
  getAvailability:     ()                  => get('/api/v1/interviewer/availability'),
  addAvailability:     (body)              => post('/api/v1/interviewer/availability', body),
  removeAvailability:  (slotId)            => del(`/api/v1/interviewer/availability/${slotId}`),
};

// ─── Admin Interviewer Management ───────────────────────────────
const adminInterviewers = {
  list:                ()                  => get('/api/v1/admin/interviewers'),
  create:              (body)              => post('/api/v1/admin/interviewers', body),
  update:              (userId, body)      => put(`/api/v1/admin/interviewers/${userId}`, body),
  revoke:              (userId)            => del(`/api/v1/admin/interviewers/${userId}`),
};

// ─── Coach (Step 33) ────────────────────────────────────────────
const coach = {
  createConversation:  ()                  => post('/api/v1/coach/conversations'),
  listConversations:   (params)            => get('/api/v1/coach/conversations', { params }),
  getConversation:     (id, params)        => get(`/api/v1/coach/conversations/${id}`, { params }),
  sendMessage:         (id, body)          => post(`/api/v1/coach/conversations/${id}/messages`, body),
  archiveConversation: (id)                => patch(`/api/v1/coach/conversations/${id}/archive`),
  listActionItems:     (params)            => get('/api/v1/coach/action-items', { params }),
  updateActionItem:    (id, body)          => patch(`/api/v1/coach/action-items/${id}`, body),
  quickAdvice:         (body)              => post('/api/v1/coach/quick-advice', body),
};

// ─── Job Hunt Agents (/api/v1/agents) ─────────────────────────────
const agents = {
  listArchetypes:      ()                         => get('/api/v1/agents/archetypes'),
  listProfiles:        ()                         => get('/api/v1/agents/profiles'),
  createProfile:       (body)                     => post('/api/v1/agents/profiles', body),
  getProfile:          (id)                       => get(`/api/v1/agents/profiles/${id}`),
  updateProfile:       (id, body)                 => patch(`/api/v1/agents/profiles/${id}`, body),
  archiveProfile:      (id)                       => del(`/api/v1/agents/profiles/${id}`),
  saveOnboarding:      (id, body)                 => post(`/api/v1/agents/profiles/${id}/onboarding`, body),
  getDashboard:        (id)                       => get(`/api/v1/agents/profiles/${id}/dashboard`),
  getIntelligencePreview: ()                    => get('/api/v1/agents/intelligence-preview'),
  getProfileIntelligence: (id)                 => get(`/api/v1/agents/profiles/${id}/intelligence`),
  listCompanyPacks:    ()                       => get('/api/v1/agents/company-packs'),
  getCompanyPack:      (slug)                   => get(`/api/v1/agents/company-packs/${slug}`),
  getSkillGraph:       ()                       => get('/api/v1/agents/skill-graph'),
  getSkillHistory:     (skillKey, params)       => get(`/api/v1/agents/skill-graph/${skillKey}/history`, { params }),
  listDailyTasks:      (profileId)              => get(`/api/v1/agents/profiles/${profileId}/daily-tasks`),
  updateDailyTask:     (id, body)               => patch(`/api/v1/agents/daily-tasks/${id}`, body),
  createStudyPlan:     (profileId, body)        => post(`/api/v1/agents/profiles/${profileId}/plans`, body),
  getActiveStudyPlan:  (profileId)              => get(`/api/v1/agents/profiles/${profileId}/plans/active`),
  updatePlanDay:       (profileId, dayNumber, body) => patch(`/api/v1/agents/profiles/${profileId}/plans/days/${dayNumber}`, body),
  listAgentEvents:     (profileId, params)        => get(`/api/v1/agents/profiles/${profileId}/events`, { params }),
  markAgentEventRead:  (profileId, eventId)       => patch(`/api/v1/agents/profiles/${profileId}/events/${eventId}/read`),
  createConversation:  (profileId)                => post(`/api/v1/agents/profiles/${profileId}/conversations`),
  getConversation:     (profileId, convId, params)  => get(`/api/v1/agents/profiles/${profileId}/conversations/${convId}`, { params }),
  sendMessage:         (profileId, convId, body)  => post(`/api/v1/agents/profiles/${profileId}/conversations/${convId}/messages`, body),
  listActionItems:     (profileId, params)        => get(`/api/v1/agents/profiles/${profileId}/action-items`, { params }),
  updateActionItem:    (id, body)                 => patch(`/api/v1/agents/action-items/${id}`, body),
  submitFeedback:      (profileId, body)          => post(`/api/v1/agents/profiles/${profileId}/feedback`, body),
  getOnboardingIntake: (profileId)                => get(`/api/v1/agents/profiles/${profileId}/onboarding/intake`),
  getOnboardingGaps:   (profileId)                => get(`/api/v1/agents/profiles/${profileId}/onboarding/gaps`),
  getIntroduction:     (profileId)                => get(`/api/v1/agents/profiles/${profileId}/onboarding/introduction`),
  saveGapAnswer:       (profileId, gapKey, value) => post(`/api/v1/agents/profiles/${profileId}/onboarding/gaps/${gapKey}`, { value }),
  completeIntroduction:(profileId)                => post(`/api/v1/agents/profiles/${profileId}/onboarding/introduction/complete`),
  getAssessments:      (profileId)                => get(`/api/v1/agents/profiles/${profileId}/onboarding/assessments`),
  skipAssessments:     (profileId)                => post(`/api/v1/agents/profiles/${profileId}/onboarding/assessments/skip`),
  completeOnboarding:  (profileId)                => post(`/api/v1/agents/profiles/${profileId}/onboarding/complete`),
  getDna:              (profileId)                => get(`/api/v1/agents/profiles/${profileId}/dna`),
  getReadiness:        (profileId)                => get(`/api/v1/agents/profiles/${profileId}/readiness`),
};

// ─── Hiring Marketplace (Step 34) ───────────────────────────────
const hiring = {
  // Public
  searchJobs:              (params)                  => get('/api/v1/hiring/jobs', { params }),
  // searchJobs accepts: { search, role_type, experience_level, location_type, skills, company_slug, page, limit }
  jobsCount:               (params)                  => get('/api/v1/hiring/jobs/count', { params }),
  getJob:                  (id)                      => get(`/api/v1/hiring/jobs/${id}`),
  getJobBySlug:            (companySlug, jobSlug)    => get(`/api/v1/hiring/jobs/${companySlug}/${jobSlug}`),
  getCompanyPublic:        (slug)                    => get(`/api/v1/hiring/companies/${slug}`),
  // Guest apply (public)
  guestApplyInit:          (jobId, body, headers)     => post(`/api/v1/hiring/jobs/${jobId}/apply-guest/init`, body, { headers }),
  guestApplyVerifyOtp:     (jobId, body)              => post(`/api/v1/hiring/jobs/${jobId}/apply-guest/verify-otp`, body),
  guestApplyUploadUrl:     (jobId, body)              => post(`/api/v1/hiring/jobs/${jobId}/apply-guest/upload-url`, body),
  guestApplyComplete:      (jobId, body, headers)     => post(`/api/v1/hiring/jobs/${jobId}/apply-guest/complete`, body, { headers }),
  // Candidate
  setVisibility:           (body)                    => put('/api/v1/hiring/visibility', body),
  getVisibility:           ()                        => get('/api/v1/hiring/visibility'),
  applyToJob:              (jobId, body)             => post(`/api/v1/hiring/jobs/${jobId}/apply`, body),
  getMyApplications:       (params)                  => get('/api/v1/hiring/my-applications', { params }),
  withdrawApplication:     (id)                      => patch(`/api/v1/hiring/applications/${id}/withdraw`),
  saveJob:                 (jobId)                   => post(`/api/v1/hiring/jobs/${jobId}/save`),
  unsaveJob:               (jobId)                   => del(`/api/v1/hiring/jobs/${jobId}/save`),
  getSavedJobs:            (params)                  => get('/api/v1/hiring/saved-jobs', { params }),
  // Company
  getMyCompanies:          ()                        => get('/api/v1/hiring/my-companies'),
  registerCompany:         (body)                    => post('/api/v1/hiring/companies', body),
  updateCompany:           (id, body)                => patch(`/api/v1/hiring/companies/${id}`, body),
  inviteMember:            (companyId, body)          => post(`/api/v1/hiring/companies/${companyId}/members`, body),
  getMembers:              (companyId)                 => get(`/api/v1/hiring/companies/${companyId}/members`),
  removeMember:            (companyId, userId)         => del(`/api/v1/hiring/companies/${companyId}/members/${userId}`),
  updateMemberRole:        (companyId, userId, body)   => patch(`/api/v1/hiring/companies/${companyId}/members/${userId}`, body),
  leaveCompany:            (companyId)                 => post(`/api/v1/hiring/companies/${companyId}/leave`, {}),
  sendInvite:              (companyId, body)           => post(`/api/v1/hiring/companies/${companyId}/invites`, body),
  listInvites:             (companyId)                 => get(`/api/v1/hiring/companies/${companyId}/invites`),
  revokeInvite:            (companyId, inviteId)       => del(`/api/v1/hiring/companies/${companyId}/invites/${inviteId}`),
  getInviteByToken:        (token)                     => get(`/api/v1/hiring/invites/company/${token}`),
  acceptInvite:            (token)                     => post(`/api/v1/hiring/invites/company/${token}/accept`, {}),
  createJob:               (companyId, body)          => post(`/api/v1/hiring/companies/${companyId}/jobs`, body),
  updateJob:               (companyId, jobId, body)   => patch(`/api/v1/hiring/companies/${companyId}/jobs/${jobId}`, body),
  getCompanyJobs:          (companyId, params)        => get(`/api/v1/hiring/companies/${companyId}/jobs`, { params }),
  getJobApplications:      (companyId, jobId, params) => get(`/api/v1/hiring/companies/${companyId}/jobs/${jobId}/applications`, { params }),
  updateApplicationStatus: (companyId, appId, body)   => patch(`/api/v1/hiring/companies/${companyId}/applications/${appId}`, body),
  setDecisionOverride:     (companyId, appId, body)   => patch(`/api/v1/hiring/companies/${companyId}/applications/${appId}/decision-override`, body),
  setInterviewScore:       (companyId, appId, body)   => patch(`/api/v1/hiring/companies/${companyId}/applications/${appId}/interview-score`, body),
  markDecisionMade:        (companyId, appId)         => post(`/api/v1/hiring/companies/${companyId}/applications/${appId}/mark-decision-made`),
  getResumeDownloadUrl:    (companyId, appId)         => get(`/api/v1/hiring/companies/${companyId}/applications/${appId}/resume-download-url`),
  discoverCandidates:      (companyId, params)        => get(`/api/v1/hiring/companies/${companyId}/candidates`, { params }),
  viewCandidate:           (companyId, candidateId)   => get(`/api/v1/hiring/companies/${companyId}/candidates/${candidateId}`),
  // Admin
  adminListCompanies:      (params)                  => get('/api/v1/hiring/admin/companies', { params }),
  adminUpdateCompany:      (id, body)                => patch(`/api/v1/hiring/admin/companies/${id}`, body),
  adminListJobs:           (params)                  => get('/api/v1/hiring/admin/jobs', { params }),
  adminModerateJob:        (jobId, body)             => post(`/api/v1/hiring/admin/jobs/${jobId}/moderate`, body),
  adminListPracticeTemplates: (params)               => get('/api/v1/hiring/admin/practice-templates', { params }),
  adminModeratePracticeTemplate: (id, body)          => post(`/api/v1/hiring/admin/practice-templates/${id}/moderate`, body),
  adminCreatePracticeExperience: (body)              => post('/api/v1/hiring/admin/practice-experiences', body || {}),
  adminListInterviewerApplications: (params)         => get('/api/v1/hiring/admin/interviewer-applications', { params }),
  adminReviewInterviewerApplication: (id, body)      => post(`/api/v1/hiring/admin/interviewer-applications/${id}/review`, body),
  adminPrefillInterviewerApplication: (id)           => get(`/api/v1/hiring/admin/interviewer-applications/${id}/prefill`),
  adminEnrichInterviewerFromLinkedInText: (id, body)=> post(`/api/v1/hiring/admin/interviewer-applications/${id}/enrich-from-linkedin-text`, body),
  adminRegisterFoundingInterviewer: (id, body)       => post(`/api/v1/hiring/admin/interviewer-applications/${id}/register-interviewer`, body),
  // AI interview templates + assignments
  generateTemplateQuestions: (body)                  => post('/api/v1/hiring/templates/generate-questions', body),
  listTemplates:             (params)                => get('/api/v1/hiring/templates', { params }),
  createTemplate:            (body)                  => post('/api/v1/hiring/templates', body),
  getTemplate:               (id)                    => get(`/api/v1/hiring/templates/${id}`),
  updateTemplate:            (id, body)              => patch(`/api/v1/hiring/templates/${id}`, body),
  publishTemplate:           (id)                    => post(`/api/v1/hiring/templates/${id}/publish`),
  archiveTemplate:           (id)                    => post(`/api/v1/hiring/templates/${id}/archive`),
  cloneTemplate:             (id, body)              => post(`/api/v1/hiring/templates/${id}/clone`, body || {}),
  listAssignableTemplates:   (companyId, params)     => get(`/api/v1/hiring/companies/${companyId}/assignable-templates`, { params }),
  assignAiInterview:         (companyId, appId, body)=> post(`/api/v1/hiring/companies/${companyId}/applications/${appId}/assign-ai-interview`, body),
  listAppAiAssignments:      (companyId, appId)      => get(`/api/v1/hiring/companies/${companyId}/applications/${appId}/ai-assignments`),
  getRecommendedInterviewers:(companyId, jobId, params) => get(`/api/v1/hiring/companies/${companyId}/jobs/${jobId}/recommended-interviewers`, { params }),
  assignInterviewer:         (companyId, appId, body)=> post(`/api/v1/hiring/companies/${companyId}/applications/${appId}/assign-interviewer`, body),
  getMyAiAssignments:        ()                      => get('/api/v1/hiring/my-ai-assignments'),
  startAiAssignment:         (id)                    => post(`/api/v1/hiring/my-ai-assignments/${id}/start`),
  answerAiAssignment:        (id, body)              => post(`/api/v1/hiring/my-ai-assignments/${id}/answer`, body || {}),
  nextAiAssignmentQuestion:  (id, body)              => post(`/api/v1/hiring/my-ai-assignments/${id}/next`, body || {}),
  endAiAssignment:           (id, body)              => post(`/api/v1/hiring/my-ai-assignments/${id}/end`, body || {}),
  completeAiAssignment:      (id, body)              => post(`/api/v1/hiring/my-ai-assignments/${id}/complete`, body || {}),
  listTemplateJobs:          (params)                => get('/api/v1/hiring/template-jobs', { params }),
  assignBookingPrep:         (bookingId, body)       => post(`/api/v1/hiring/bookings/${bookingId}/prep`, body),
  getBookingPrep:            (bookingId)             => get(`/api/v1/hiring/bookings/${bookingId}/prep`),
  startBookingPrep:          (bookingId)             => post(`/api/v1/hiring/bookings/${bookingId}/prep/start`),
  completeBookingPrep:       (bookingId, body)       => post(`/api/v1/hiring/bookings/${bookingId}/prep/complete`, body || {}),
  inviteGuestRegister:       (companyId, appId, body)=> post(`/api/v1/hiring/companies/${companyId}/applications/${appId}/invite-register`, body || {}),
  previewClaimApplication:   (token)                 => get(`/api/v1/hiring/claim-application/${encodeURIComponent(token)}`),
  claimApplication:          (token)                 => post(`/api/v1/hiring/claim-application/${encodeURIComponent(token)}`),
  setTemplateDiscoverable:   (id, body)              => post(`/api/v1/hiring/templates/${id}/candidate-discoverable`, body || {}),
};

// ─── Practice interview catalog (candidate marketplace) ─────────
// ─── Campus Placement (/api/v1/campus) ───────────────────────────
const campus = {
  getProfile: () => get('/api/v1/campus/profile'),
  updateProfile: (body) => put('/api/v1/campus/profile', body),
  getPath: () => get('/api/v1/campus/path'),
  listColleges: () => get('/api/v1/campus/colleges'),
  listTargetCompanies: () => get('/api/v1/campus/target-companies'),
  getReadiness: () => get('/api/v1/campus/readiness'),
  getProgress: () => get('/api/v1/campus/progress'),
  getMockPresets: () => get('/api/v1/campus/mock-presets'),
  startAptitude: (body) => post('/api/v1/campus/aptitude/start', body || {}),
  submitAptitude: (attemptId, answers) =>
    post(`/api/v1/campus/aptitude/${attemptId}/submit`, { answers }),
  getAptitudeResult: (attemptId) => get(`/api/v1/campus/aptitude/${attemptId}/result`),
  listTpoPartners: () => get('/api/v1/campus/tpo/partners'),
  inviteTpoStudents: (partnerId, emails) =>
    post(`/api/v1/campus/tpo/${partnerId}/invite`, { emails }),
  getTpoMetrics: (partnerId) => get(`/api/v1/campus/tpo/${partnerId}/metrics`),
  getCurriculum: () => get('/api/v1/campus/curriculum'),
  getCurriculumCoverage: () => get('/api/v1/campus/curriculum/coverage'),
  getDrillMeta: () => get('/api/v1/campus/drills/meta'),
  startDrill: (body) => post('/api/v1/campus/drills/start', body || {}),
  submitDrill: (attemptId, answers) =>
    post(`/api/v1/campus/drills/${attemptId}/submit`, { answers }),
  getDrillResult: (attemptId) => get(`/api/v1/campus/drills/${attemptId}/result`),
};

const practiceCatalog = {
  list:   (params)     => get('/api/v1/practice/interviews', { params }),
  get:    (id)         => get(`/api/v1/practice/interviews/${id}`),
  start:  (id)         => post(`/api/v1/practice/interviews/${id}/start`),
  answer: (id, body)   => post(`/api/v1/practice/interviews/${id}/answer`, body || {}),
  next:   (id, body)   => post(`/api/v1/practice/interviews/${id}/next`, body || {}),
  end:    (id, body)   => post(`/api/v1/practice/interviews/${id}/end`, body || {}),
  rate:   (id, body)   => post(`/api/v1/practice/interviews/${id}/rate`, body || {}),
  createExperience:          (body)     => post('/api/v1/practice/interviews/experience', body),
  updateExperience:          (id, body) => patch(`/api/v1/practice/interviews/experience/${id}`, body),
  publishExperience:         (id, body) => post(`/api/v1/practice/interviews/experience/${id}/publish`, body || {}),
  refineExperienceQuestions: (body)     => post('/api/v1/practice/interviews/experience/refine-questions', body),
};

// ─── Public: founding-interviewer application (no auth) ─────────
const interviewerApply = {
  submit: (body) => post('/api/v1/interviewer-apply', body),
};

// ─── Public SEO content (no auth) ───────────────────────────────
const publicContent = {
  list: (params) => get('/api/public/content', { params }),
  getBySlug: (slug) => get(`/api/public/content/${encodeURIComponent(slug)}`),
};

// ─── Default Export ──────────────────────────────────────────────
const api = {
  auth,
  interview,
  resume,
  billing,
  ai,
  featureFlags,
  integrity,
  learning,
  progress,
  notifications,
  community,
  career,
  account,
  admin,
  partners,
  rewards,
  feedback,
  reviews,
  analytics,
  privacy,
  legal,
  tts,
  stt,
  leaderboard,
  xp,
  achievements,
  rooms,
  aiObserver,
  scheduling,
  replay,
  referrals,
  marketplace,
  interviewer,
  adminInterviewers,
  coach,
  agents,
  hiring,
  practiceCatalog,
  campus,
  publicContent,
  interviewerApply,
  demoStudio,
  games,
};

export default api;
